import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingBlock } from '@/components/PageHeader';
import { apiFetch } from '@/lib/api/client';
import { today } from '@/lib/format';
import { useSites } from '@/lib/queries';
import type { DashboardSite, SiteLevel } from '@/pages/safe/safeDashboardApi';
import { JOURNAL_TYPES, useSafeJournals, type JournalSummary, type JournalType } from '@/pages/safe/safeJournalApi';

import { FixLink, FixReasons } from './FixLink';
import { useSiteInspections, useSiteTbm } from './siteSafeApi';

/**
 * 현장 홈(DOC-136 §3 · 세이프유 웍스 「현장 안전 대시보드」). 지금 있는 자료로 서는 칸만 세운다 — 위험성평가 · 일일 점검 ·
 * 온열 점검 칸은 그 자료가 생기는 단계에 더한다.
 *
 * <p>판정(위험 · 주의 · 양호와 사유)은 대시보드 표의 그 현장 한 줄과 같다(서버 SafeDashboardService.site).
 * 「이번 달」 기준: 안전보건일지는 하루 한 장, 순회점검은 2일에 1회(대시보드와 같은 주기).
 */
export function SiteHomePage({ siteId }: { siteId: string }) {
  const navigate = useNavigate();
  const sites = useSites();
  const siteName = sites.data?.find((site) => site.id === siteId)?.name ?? '';
  const status = useQuery({
    queryKey: ['safe-site', siteId, 'status'],
    queryFn: () => apiFetch<DashboardSite | null>(`/v1/safe/sites/${siteId}/status`),
  });

  const now = today();
  const monthStart = `${now.slice(0, 8)}01`;
  const from = minDate(monthStart, daysAgo(now, 30));
  const patrol = useSafeJournals('PATROL', siteId, from, '');
  const joint = useSafeJournals('JOINT', siteId, from, '');
  const education = useSafeJournals('EDUCATION', siteId, from, '');
  const health = useSafeJournals('HEALTH', siteId, from, '');
  const tbmLogs = useSafeJournals('TBM', siteId, from, '');
  const risks = useSafeJournals('RISK', siteId, from, '');
  const tbm = useSiteTbm(siteId);
  const inspections = useSiteInspections(siteId);

  if (status.isPending) return <LoadingBlock />;
  const site = status.data ?? null;
  const journals = [
    ...(patrol.data ?? []),
    ...(joint.data ?? []),
    ...(education.data ?? []),
    ...(health.data ?? []),
    ...(tbmLogs.data ?? []),
    ...(risks.data ?? []),
  ];
  const thisMonth = (type: JournalType) => journals.filter((j) => j.journalType === type && j.journalDate >= monthStart).length;
  const dayOfMonth = Number(now.slice(8, 10));
  const openActions = (inspections.data ?? [])
    .flatMap((round) => round.actions.map((action) => ({ ...action, roundNo: round.roundNo })))
    .filter((action) => action.status !== '확인완료')
    .sort((a, b) => (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'));
  const todayTbm = (tbm.data ?? []).filter((row) => row.tbmDate === now);
  const todayTbmLogs = (tbmLogs.data ?? []).filter((row) => row.journalDate === now).length;
  /**
   * 미조치 칸의 수(서버)는 포털 시정조치 「지적」 + 그 현장 마지막 순회점검일지의 「미흡」 이다(SafeDashboardMapper open_checks).
   * 목록도 같은 둘을 보인다 — 칸은 1건인데 목록이 「없음」 이면 안 된다.
   */
  const latestPatrol = [...(patrol.data ?? [])].sort((a, b) => b.journalDate.localeCompare(a.journalDate))[0] ?? null;
  const patrolOpen = latestPatrol && latestPatrol.openCount > 0 ? latestPatrol : null;
  const recent = [...journals].sort((a, b) => (b.updatedAt ?? b.journalDate).localeCompare(a.updatedAt ?? a.journalDate)).slice(0, 8);
  const go = (key: string) => navigate(`/sites/${encodeURIComponent(siteId)}/${key}`);
  const open = (row: JournalSummary) => go(`${JOURNAL_TYPES[row.journalType].path.slice(1)}?open=${row.id}`);

  return (
    <div className="space-y-5">
      <style>{STYLES}</style>
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 style={{ fontSize: '26px', fontWeight: 800, margin: 0 }}>{siteName}</h1>
          {site && <span className={`badge ${LEVELS[site.level].badge}`}>{LEVELS[site.level].label}</span>}
        </div>
        {site && site.reasons.length > 0 && (
          <div className="mt-1 text-ui-sm" style={{ color: 'var(--danger)' }}>
            <FixReasons siteId={siteId} reasons={site.reasons} />
          </div>
        )}
      </div>

      <section className="card sh-card">
        <h2>오늘의 안전활동</h2>
        <div className="sh-tiles">
          <Tile
            label="TBM"
            value={site?.tbmToday ? '작성' : <FixLink siteId={siteId} text="TBM 미작성">미작성</FixLink>}
            warn={!site?.tbmToday}
            sub={
              site?.tbmToday
                ? [todayTbmLogs > 0 ? `TBM 일지 ${todayTbmLogs}장` : null, site.tbmSigners > 0 ? `포털 서명 ${site.tbmSigners}명` : null]
                    .filter(Boolean)
                    .join(' · ') || null
                : null
            }
            action="TBM 일지 작성"
            onAction={() => go('tbm?new=1')}
          />
          <Tile
            label="순회점검"
            value={lateText(siteId, '순회점검', site?.patrolLate ?? null)}
            warn={site?.patrolLate !== 0}
            sub={site?.lastPatrol ? `최근 ${site.lastPatrol}` : null}
            action="순회점검일지 작성"
            onAction={() => go('patrol?new=1')}
          />
          <Tile
            label="합동점검"
            value={lateText(siteId, '합동점검', site?.jointLate ?? null)}
            warn={site?.jointLate !== 0}
            sub={site?.lastJoint ? `최근 ${site.lastJoint}` : null}
            action="합동점검일지 작성"
            onAction={() => go('joint?new=1')}
          />
          <Tile
            label="미조치"
            value={
              site && site.openActions + site.openChecks > 0 ? (
                <FixLink siteId={siteId} text="미조치">{`${site.openActions + site.openChecks}건`}</FixLink>
              ) : (
                '0건'
              )
            }
            warn={!!site && site.openActions + site.openChecks > 0}
            sub={site && site.overdueActions > 0 ? `기한 지남 ${site.overdueActions}건` : null}
            action="시정조치 보기"
            onAction={() => go('actions')}
          />
        </div>
      </section>

      <div className="sh-two">
        <section className="card sh-card">
          <h2>이번 달 안전활동</h2>
          <Bar label="TBM 일지" value={thisMonth('TBM')} unit="장" />
          <Bar label="안전보건일지" value={thisMonth('HEALTH')} goal={dayOfMonth} unit="일" />
          <Bar label="순회점검일지" value={thisMonth('PATROL')} goal={Math.ceil(dayOfMonth / 2)} unit="회" />
          <Bar label="안전교육일지" value={thisMonth('EDUCATION')} unit="회" />
          <Bar label="합동점검일지" value={thisMonth('JOINT')} unit="회" />
          <Bar label="위험성평가" value={thisMonth('RISK')} unit="회" />
        </section>

        <section className="card sh-card">
          <h2>
            미완료 시정조치
            <button type="button" className="sh-more" onClick={() => go('actions')}>
              전체 ›
            </button>
          </h2>
          {openActions.length === 0 && !patrolOpen ? (
            <Empty />
          ) : (
            <ul className="sh-list">
              {patrolOpen && (
                <li onClick={() => open(patrolOpen)}>
                  <span className="sh-grow">순회점검일지 미흡 {patrolOpen.openCount}건</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{patrolOpen.journalDate}</span>
                  <span className="badge badge-yellow">미흡</span>
                </li>
              )}
              {openActions.slice(0, patrolOpen ? 5 : 6).map((action) => {
                const late = action.status === '지적' && action.dueDate !== null && action.dueDate < now;
                return (
                  <li key={action.id} onClick={() => go('actions')}>
                    <span className="sh-grow">{action.finding}</span>
                    <span style={{ color: late ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: late ? 700 : 400 }}>
                      {action.dueDate ?? '—'}
                    </span>
                    <span className={`badge ${action.status === '지적' ? 'badge-red' : 'badge-yellow'}`}>{action.status}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <div className="sh-two">
        <section className="card sh-card">
          <h2>최근 활동</h2>
          {recent.length === 0 ? (
            <Empty />
          ) : (
            <ul className="sh-list">
              {recent.map((row) => (
                <li key={row.id} onClick={() => open(row)}>
                  <span className="badge badge-blue">{JOURNAL_TYPES[row.journalType].label}</span>
                  <span className="sh-grow">{row.journalDate}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{row.authorName ?? ''}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card sh-card">
          <h2>
            협력사 TBM
            <button type="button" className="sh-more" onClick={() => go('vendor-tbm')}>
              전체 ›
            </button>
          </h2>
          {todayTbm.length === 0 ? (
            <Empty />
          ) : (
            <ul className="sh-list">
              {todayTbm.map((row) => (
                <li key={row.id} onClick={() => go('vendor-tbm')}>
                  <span className="sh-grow">{row.workSummary ?? '—'}</span>
                  <span style={{ fontWeight: 600, color: row.signedCount < row.requiredCount ? '#b45309' : undefined }}>
                    서명 {row.signedCount}/{row.requiredCount}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function lateText(siteId: string, label: string, late: number | null): ReactNode {
  if (late === 0) return '준수';
  const text = late === null ? `${label} 미실시` : `${label} 지난 ${late}일`;
  return <FixLink siteId={siteId} text={text}>{late === null ? '미실시' : `지난 ${late}일`}</FixLink>;
}

function Tile({
  label,
  value,
  warn,
  sub,
  action,
  onAction,
}: {
  label: string;
  value: ReactNode;
  warn: boolean;
  sub: string | null;
  action: string;
  onAction: () => void;
}) {
  return (
    <div className="sh-tile">
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: warn ? 'var(--danger)' : '#15803d' }}>{value}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', minHeight: '18px' }}>{sub ?? ''}</div>
      <button type="button" className="btn-secondary w-full justify-center" style={{ fontSize: '13px' }} onClick={onAction}>
        {action}
      </button>
    </div>
  );
}

/** 이행 막대. 기준(goal)이 없는 일지는 횟수만. */
function Bar({ label, value, goal, unit }: { label: string; value: number; goal?: number; unit: string }) {
  const rate = goal ? Math.min(100, Math.round((value / Math.max(1, goal)) * 100)) : null;
  return (
    <div className="sh-bar">
      <span>{label}</span>
      <span className="sh-track">{rate !== null && <span style={{ width: `${rate}%`, background: rate >= 80 ? '#15803d' : rate >= 50 ? '#d97706' : '#dc2626' }} />}</span>
      <span style={{ textAlign: 'right', fontWeight: 600 }}>
        {value}
        {goal ? `/${goal}` : ''}
        {unit}
        {rate !== null && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {rate}%</span>}
      </span>
    </div>
  );
}

function Empty() {
  return <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '16px 0', textAlign: 'center' }}>없음</div>;
}

function daysAgo(day: string, n: number): string {
  const at = new Date(`${day}T00:00:00`);
  at.setDate(at.getDate() - n);
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

function minDate(a: string, b: string): string {
  return a < b ? a : b;
}

const LEVELS: Record<SiteLevel, { label: string; badge: string }> = {
  DANGER: { label: '위험', badge: 'badge-red' },
  CAUTION: { label: '주의', badge: 'badge-yellow' },
  GOOD: { label: '양호', badge: 'badge-green' },
  ENDED: { label: '종료', badge: 'badge-gray' },
};

const STYLES = `
.sh-card { padding: 18px 20px; border-radius: 14px; }
.sh-card h2 { display: flex; align-items: center; justify-content: space-between; font-size: 15px; font-weight: 700; margin: 0 0 14px; }
.sh-more { background: none; border: none; font-size: 12px; font-weight: 400; color: var(--text-secondary); cursor: pointer; }
.sh-tiles { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.sh-tile { display: flex; flex-direction: column; gap: 6px; padding: 14px; border: 1px solid var(--border); border-radius: 10px; }
.sh-two { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.sh-bar { display: grid; grid-template-columns: 96px 1fr 120px; align-items: center; gap: 10px; font-size: 13px; padding: 7px 0; }
.sh-track { height: 12px; background: var(--bg-hover, #f1f5f9); border-radius: 4px; overflow: hidden; }
.sh-track span { display: block; height: 100%; border-radius: 0 4px 4px 0; }
.sh-list { list-style: none; margin: 0; padding: 0; }
.sh-list li { display: flex; align-items: center; gap: 10px; padding: 9px 4px; font-size: 13px; border-bottom: 1px solid var(--border); cursor: pointer; }
.sh-list li:last-child { border-bottom: none; }
.sh-list li:hover { background: var(--bg-hover, #f8fafc); }
.sh-grow { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 900px) {
  .sh-tiles { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .sh-two { grid-template-columns: minmax(0, 1fr); }
}
`;
